import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { BRAND } from "@/constants/brand";
import { SiteFooter as PublicFooter, SiteNav as PublicNav } from "@/components/media/SiteNav";
import { Eyebrow } from "@/components/ladder/Offers";
import { LeadForm } from "@/components/ladder/LeadForm";
import { ArrowRight, Check } from "lucide-react";

const STORAGE_KEY = "smash_guide_unlocked";

export default function Guide() {
  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "true"
  );
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    if (unlocked) api.get("/guide").then(({ data }) => setGuide(data));
  }, [unlocked]);

  const unlock = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setUnlocked(true);
  };

  return (
    <div data-testid="guide-page">
      <PublicNav active="guide" />

      <div className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 photo-band opacity-25"
          style={{ backgroundImage: `url(${BRAND.photos.lemonPurple})` }}
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/88" />
        <div className="relative mx-auto max-w-3xl px-5 py-16 md:px-10 md:py-24">
          <Eyebrow>LEVEL 01 · FREE</Eyebrow>
          <h1 className="neon mt-4 text-4xl leading-[0.95] sm:text-5xl">
            Thinking about starting a <span className="text-primary">drink pop-up?</span>
          </h1>
          <p className="mt-5 text-base text-muted-foreground md:text-lg">
            Get a behind-the-scenes look at what we've learned building Smash Lemonade from a simple
            pop-up into a growing event-based beverage brand.
          </p>
        </div>
      </div>

      {!unlocked ? (
        <div className="mx-auto max-w-lg px-5 py-16 md:py-24">
          <div className="border border-border bg-[#141414] p-6 md:p-8">
            <h2 className="display text-3xl uppercase">Read it now</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Tell us where to send your name and we'll open the guide right here.
            </p>
            <div className="mt-6">
              <LeadForm
                source="free_starter_guide"
                productKey="starter_guide"
                fields={["first_name", "email"]}
                submitLabel="GET THE FREE GUIDE"
                testId="guide-lead-form"
                onDone={unlock}
              />
            </div>
            <p className="mono mt-4 text-[10px] tracking-widest text-muted-foreground">
              FREE GUIDE. NO CREDIT CARD REQUIRED.
            </p>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-3xl px-5 py-14 md:px-10 md:py-20" data-testid="guide-content">
          <div className="border border-primary/60 bg-[#191603] p-6 text-center">
            <p className="display neon text-2xl md:text-3xl">
              Welcome to Smash Academy. Your first lesson starts now.
            </p>
          </div>

          {!guide ? (
            <p className="mono mt-10 text-sm text-muted-foreground">Opening the guide…</p>
          ) : (
            <>
              <p className="mt-12 text-base leading-relaxed text-white md:text-lg">{guide.intro}</p>
              <div className="mt-12 space-y-14">
                {(guide.sections || []).map((s, i) => (
                  <section key={s.id} data-testid={`guide-section-${s.id}`}>
                    <Eyebrow>{String(i + 1).padStart(2, "0")}</Eyebrow>
                    <h2 className="mt-2 text-3xl sm:text-4xl">{s.title}</h2>
                    <p className="mt-3 text-base leading-relaxed text-muted-foreground">{s.body}</p>
                    <ul className="mt-5 space-y-2">
                      {(s.items || []).map((item) => (
                        <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                          <Check size={15} className="mt-0.5 shrink-0 text-primary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
              <p className="brand-script mt-16 text-3xl text-primary">{guide.closing}</p>
            </>
          )}

          <div className="mt-16 border border-border bg-[#141414] p-6 md:p-8">
            <Eyebrow>YOUR NEXT STEP</Eyebrow>
            <h3 className="display mt-2 text-3xl uppercase">Know your numbers next</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              The Smash Event Profit Calculator tells you whether an event makes money before you pay
              the vendor fee.
            </p>
            <Link
              to="/start"
              data-testid="guide-next-step"
              className="display group mt-6 inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-lg uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
            >
              See what's next
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      )}

      <PublicFooter />
    </div>
  );
}
